import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getPromoteurs,
  handleSyncPromoteur,
  updateSinglePromoteur,
  uploadPromoteurLogo,
} from "../../../redux/actions/promoteursActions";
import SwitchButton from "../../../components/commons/SwitchBtn";
import axios from "axios";
import toast from "react-hot-toast";
import { closeModal } from "../../../redux/actions/modalActions";
import mainAxios from "../../../redux/api/mainAxios";

const EditPromoteurModal = ({ promoteur, secteurs }) => {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useSelector((state) => state.userAuthReducer);
  const [formData, setFormData] = useState({
    name: promoteur.name || "",
    url: promoteur.url || "",
    tag: promoteur.tag || "",
    mandat: promoteur.mandat || "",
    isEdited: true,
    promoteur_accesses: promoteur.promoteur_accesses.map((access) => ({
      id: access.id,
      documentId: access.documentId,
      url_promoteur: access.url_promoteur || "",
      login: access.login || "",
      password: access.password || "",
      secteur: access.secteur || null,
    })),
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePhotoClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedImage(URL.createObjectURL(file));
      dispatch(uploadPromoteurLogo(promoteur.documentId, file));
    }
  };

  useEffect(() => {
    return () => {
      if (selectedImage) {
        URL.revokeObjectURL(selectedImage);
      }
    };
  }, [selectedImage]);

  const handleSynchroniser = (id) => {
    dispatch(handleSyncPromoteur(id));
  };

  const handleUpdatePromoteur = async (id) => {
    setIsLoading(true);

    const payload = {
      name: formData.name,
      url: formData.url,
      tag: formData.tag,
      mandat: formData.mandat,
      isEdited: formData.isEdited,
    };

    try {
      await dispatch(updateSinglePromoteur(id, payload));
    } catch (error) {
      console.error("Failed to update promoteur:", error);
      alert("Failed to update promoteur. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };
  const handleAccessChange = (index, field, value) => {
    setFormData((prev) => {
      const updatedAccesses = [...prev.promoteur_accesses];

      if (field === "secteur") {
        updatedAccesses[index][field] = value
          ? { documentId: value.documentId }
          : null;
      } else {
        updatedAccesses[index][field] = value;
      }

      return {
        ...prev,
        promoteur_accesses: updatedAccesses,
      };
    });
  };
  const handleUpdatePromoteurAccesses = async () => {
    setIsLoading(true);

    try {
      const token = localStorage.getItem("token");
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      // Create a copy of promoteur_accesses to avoid mutating state directly
      const updatedAccesses = [...formData.promoteur_accesses];

      for (let i = 0; i < updatedAccesses.length; i++) {
        const access = updatedAccesses[i];

        if (access.documentId) {
          // Update existing access
          const payload = {
            data: {
              url_promoteur: access.url_promoteur,
              login: access.login,
              password: access.password,
              secteur: access.secteur?.documentId || null,
            },
          };

          await mainAxios.put(
            `api/promoteur-accesses/${access.documentId}`,
            payload,
            config
          );
        } else {
          // Create new access
          const createdAccess = await handleCreatePromoteurAccess(access);
          updatedAccesses[i] = createdAccess; // Replace the temporary access with the created one
        }
      }

      // Update the state with the new access data (including documentId)
      setFormData((prev) => ({
        ...prev,
        promoteur_accesses: updatedAccesses,
      }));
      dispatch(getPromoteurs(1, 25));
      toast.success("Accès mis à jour avec succès!");
    } catch (error) {
      console.error("Failed to update promoteur accesses:", error);
      toast.error("Échec de la mise à jour des accès. Veuillez réessayer.");
    } finally {
      setIsLoading(false);
    }
  };
  const handleAddAccess = () => {
    {
      setFormData((prev) => ({
        ...prev,
        promoteur_accesses: [
          ...prev.promoteur_accesses,
          {
            id: `new-${prev.promoteur_accesses.length + 1}`,
            documentId: null,
            url_promoteur: "",
            login: "",
            password: "",
            secteur: null,
          },
        ],
      }));
    }
  };
  const handleCreatePromoteurAccess = async (access) => {
    const token = localStorage.getItem("token");
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };

    const payload = {
      data: {
        url_promoteur: access.url_promoteur,
        login: access.login,
        password: access.password,
        secteur: access.secteur?.documentId || null,
        promoteur: promoteur.documentId,
      },
    };

    try {
      const response = await mainAxios.post(
        `api/promoteur-accesses`,
        payload,
        config
      );

      // Return the created access with its documentId
      return response.data.data;
    } catch (error) {
      console.error("Failed to create promoteur access:", error);
      throw error;
    }
  };
  const handleDeletePromoteurAccess = async (id) => {
    const token = localStorage.getItem("token");
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };

    try {
      await mainAxios.delete(`api/promoteur-accesses/${id}`, config);

      setFormData((prev) => ({
        ...prev,
        promoteur_accesses: prev.promoteur_accesses.filter(
          (access) => access.documentId !== id
        ),
      }));

      toast.success("Accès supprimé avec succès!");
    } catch (error) {
      console.error("Failed to delete promoteur access:", error);
      toast.error("Échec de la suppression de l'accès. Veuillez réessayer.");
    }
  };
  const handleDeletePromoteur = async (id) => {
    toast(
      (t) => (
        <div className="d-flex flex-column gap10">
          <span>Êtes-vous sûr de vouloir supprimer ce promoteur ?</span>
          <div className="d-flex justify-content-end gap10">
            <button
              className="bg-soft-grey radius5 px-20 py-1 white border-0"
              onClick={() => toast.dismiss(t.id)}
            >
              Annuler
            </button>
            <button
              className="bg-pink radius5 px-20 py-1 white border-0"
              onClick={async () => {
                toast.dismiss(t.id);
                const token = localStorage.getItem("token");
                const config = {
                  headers: {
                    Authorization: `Bearer ${token}`,
                  },
                };

                try {
                  await mainAxios.delete(`api/promoteurs/${id}`, config);

                  toast.success("Promoteur supprimé avec succès!");
                } catch (error) {
                  console.error("Failed to delete promoteur:", error);
                  toast.error(
                    "Échec de la suppression du promoteur. Veuillez réessayer."
                  );
                } finally {
                  dispatch(getPromoteurs());
                  dispatch(closeModal());
                }
              }}
            >
              Supprimer
            </button>
          </div>
        </div>
      ),
      {
        duration: Infinity,
        position: "top-center",
      }
    );
  };
  return (
    <>
      <div className="d-flex justify-content-start gap20">
        <div className="d-flex flex-column ">
          <p className="semibold pink fs16">Logo</p>
          <hr />
          <div className="position-relative">
            <img
              src={
                selectedImage ||
                (promoteur.logo?.url
                  ? `${process.env.REACT_APP_IMAGE_END_POINT}${promoteur.logo?.url}`
                  : "./assets/imgNotFound.webp")
              }
              alt=""
              height={200}
              width={200}
              className="mb-10"
              style={{ objectFit: "contain" }}
            />
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept="image/*"
              style={{ display: "none" }}
            />
            <button
              className="bg-pink radius5 px-20 py-1 white border-0 promoteurChangePhoto"
              onClick={handlePhotoClick}
            >
              Changer la photo
            </button>
          </div>
        </div>
        <div className="w100 d-flex flex-column">
          <div>
            <p className="semibold pink fs16">Fiche du promoteur</p>
            <hr />
          </div>
          <div className="d-flex flex-column gap20">
            <div>
              <label className="medium darkGrey fs12 mb-10">Nom</label>
              <input
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="w100 customInput"
              />
            </div>
            <div>
              <label className="medium darkGrey fs12 mb-10 ">URL</label>
              <div className="d-flex align-items-center gap10">
                <input
                  name="url"
                  value={formData.url}
                  onChange={handleInputChange}
                  className="w100 customInput"
                />
                <a
                  href={formData.url}
                  target="_blank"
                  className="hoverTransition"
                  rel="noopener noreferrer"
                >
                  <img
                    src="/icons/external-link.svg"
                    alt=""
                    height={24}
                    width={24}
                  />
                </a>
              </div>
            </div>
            <div>
              <label className="medium darkGrey fs12 mb-10">Tag</label>
              <input
                name="tag"
                value={formData.tag}
                onChange={handleInputChange}
                className="w100 customInput"
              />
            </div>
            <div>
              <label className="medium darkGrey fs12 mb-10">Mandat</label>
              <input
                name="mandat"
                value={formData.mandat}
                onChange={handleInputChange}
                className="w100 customInput"
              />
            </div>
            <div className="mb-10 d-flex align-items-center justify-content-between">
              <div className="">
                <label className="medium darkGrey fs12 mb-10">Origine</label>
                <p>{promoteur.origine}</p>
              </div>
              <div className="d-flex flex-column">
                <label className="medium darkGrey fs12 mb-10">
                  Synchronisable
                </label>
                <SwitchButton
                  isActive={!promoteur.isEdited}
                  itemId={promoteur.documentId}
                  endpoint="api/promoteurs"
                  fieldName="isEdited"
                  synchable={true}
                />
              </div>
            </div>
          </div>
          <button
            className={
              isLoading || formData.name === ""
                ? "bg-soft-grey radius5 px-20 py-1 white border-0 hoverOpacity mb-10"
                : "bg-pink radius5 px-20 py-1 white border-0 hoverOpacity mb-10"
            }
            disabled={isLoading || formData.name === ""}
            onClick={() => handleUpdatePromoteur(promoteur.documentId)}
          >
            Enregistrer
          </button>

          <button
            className={
              promoteur.isEdited
                ? "bg-soft-grey radius5 px-20 py-1 white border-0 mb-10"
                : "bg-pink radius5 px-20 py-1 white border-0 hoverOpacity mb-10"
            }
            onClick={() => handleSynchroniser(promoteur.documentId)}
            disabled={promoteur.isEdited}
          >
            Synchroniser
          </button>
          <button
            className={
              promoteur.isEdited
                ? "bg-soft-grey radius5 px-20 py-1 white border-0 mb-10"
                : "bg-pink radius5 px-20 py-1 white border-0 hoverOpacity mb-10"
            }
            onClick={() => handleDeletePromoteur(promoteur.documentId)}
            disabled={promoteur.isEdited}
          >
            Supprimer
          </button>
        </div>
      </div>
      {user.profile === "Administrateur" ||
      (user.profile === "Collaborateur" && user.assignSecteur) ? (
        <div className="">
          <p className="semibold pink fs16">Access</p>
          <hr />
          <div className="d-flex flex-column gap10 mb-20">
            {formData.promoteur_accesses.map((access, index) => (
              <div key={access.documentId} className="d-flex gap10">
                {/* Secteur Dropdown */}
                <div className="d-flex flex-column justify-content-between">
                  <label className="medium darkGrey fs12 mb-10">Secteur</label>
                  <select
                    value={access.secteur?.documentId || ""}
                    onChange={(e) => {
                      const selectedSecteur = secteurs.data.find(
                        (secteur) => secteur.documentId === e.target.value
                      );
                      handleAccessChange(index, "secteur", selectedSecteur);
                    }}
                    className="w100 customInput"
                  >
                    <option value="">Select a secteur</option>
                    {secteurs.data.map((secteur) => (
                      <option
                        key={secteur.documentId}
                        value={secteur.documentId}
                      >
                        {secteur.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* URL */}
                <div className="d-flex flex-column">
                  <label className="medium darkGrey fs12 mb-10">URL</label>
                  <input
                    name="url_promoteur"
                    value={access.url_promoteur}
                    onChange={(e) =>
                      handleAccessChange(index, "url_promoteur", e.target.value)
                    }
                    className="w100 customInput"
                  />
                </div>

                {/* Login */}
                <div className="d-flex flex-column">
                  <label className="medium darkGrey fs12 mb-10">Login</label>
                  <input
                    name="login"
                    value={access.login}
                    onChange={(e) =>
                      handleAccessChange(index, "login", e.target.value)
                    }
                    className="w100 customInput"
                  />
                </div>

                {/* Password */}
                <div className="d-flex flex-column">
                  <label className="medium darkGrey fs12 mb-10">Password</label>
                  <input
                    name="password"
                    value={access.password}
                    onChange={(e) =>
                      handleAccessChange(index, "password", e.target.value)
                    }
                    className="w100 customInput"
                  />
                </div>
                {/* Delete btn */}
                <button
                  className="bg-none border-0 hoverOpacity d-flex align-items-end mb-10 "
                  onClick={() => handleDeletePromoteurAccess(access.documentId)}
                  disabled={isLoading}
                >
                  <img
                    src="/icons/bin.svg"
                    alt=""
                    height={24}
                    width={24}
                    className="shakeShake"
                  />
                </button>
              </div>
            ))}
          </div>

          <div className="d-flex flex-column ">
            {/* Button to update promoteur_accesses */}
            {formData.promoteur_accesses.length > 0 && (
              <button
                className="bg-pink radius5 px-20 py-1 white border-0 hoverOpacity mb-10"
                onClick={() => handleUpdatePromoteurAccesses()}
                disabled={isLoading}
              >
                Mettre à jour les Accès
              </button>
            )}
            {/* Button to add a new access */}
            <button
              className="bg-pink radius5 px-20 py-1 white border-0 hoverOpacity "
              onClick={handleAddAccess}
              disabled={isLoading}
            >
              Ajouter un Accès
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default EditPromoteurModal;
